import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react'
import usePagination from '@/hooks/usePagination'
import Pagination from '@UI/New/Pagination'
import { directionOrdersApi, reportAPI, supportAPI } from '@/api'
import { IFinancialTransactionsReportData } from './utils/types'
import ExerciseHomeworkReportTable from './FinancialTransactionsReportTable/FinancialTransactionsReportTable'
import cn from './index.module.css'
import { FinancialTransactionsReportFilters } from './FinancialTransactionsReportFilters'
import Button from '@UI/New/Button'
import { IcoDownload } from '@/utils/icons'
import { useDownloadFile } from '@/hooks/useDownloadFile'
import { Modal } from 'antd'
import { useSelector } from 'react-redux'
import { selectProfile } from '@/store/profile.reducer'
import { useAddAlert } from '@/hooks/useAddAlert'

interface IFinancialTransactionsReport {
  userId: number
  filters?: IFilters
  setFilters?: Dispatch<SetStateAction<IFilters>>
  showDownload?: boolean
}

export interface IFilters {
  directionId: number | null | string;
  balanceId: string | null;
  status: string | null;
}

const initFilters: IFilters = {
  directionId: null,
  balanceId: null,
  status: null,
};

const MANUAL_PAYMENT_CONFIRM_ROLES = [
  'admin',
  'junior-admin',
  'personal-manager',
  'hr-manager',
  'seminarian',
  'senior-seminarian',
  'sales-manager',
  'head-of-sales',
]

const FinancialTransactionsReport: FC<IFinancialTransactionsReport> = ({
  userId,
  filters: controlledFilters,
  setFilters: setControlledFilters,
  showDownload = true,
}) => {
  const profile = useSelector(selectProfile)
  const addAlert = useAddAlert()
  const { download, loading: downloadLoading } = useDownloadFile()
  const [sorting, setSorting] = useState<
    { key: string; order: 'asc' | 'desc' | 'none' }[]
  >(
    [].map((item) => {
      return { key: item, order: 'none' }
    }),
  )
  
  const [localFilters, setLocalFilters] = useState<IFilters>(initFilters);
  const filters = controlledFilters ?? localFilters
  const setFilters = setControlledFilters ?? setLocalFilters

  const onSortingClick = (key: string) => {
    setSorting((prev) =>
      prev.map((item) => {
        const order =
          key === item.key
            ? item.order === 'none'
              ? 'asc'
              : item.order === 'asc'
              ? 'desc'
              : 'none'
            : 'none'

        return { ...item, order: order }
      }),
    )
  }

  const getFilters = () => {
    const filterParams: Record<string, string> = {}

    filterParams['user-id'] = String(userId)
    if (filters.balanceId || filters.directionId) {
      filterParams['direction_id'] = String(filters.balanceId || filters.directionId)
    }
    if (filters.status) filterParams.status = filters.status

    return filterParams
  }

  const handleDownloadExcel = () => {
    download({
      request: () =>
        directionOrdersApi.getHistoryExcel({
          id: userId,
          ...(filters.balanceId || filters.directionId
            ? { direction_id: filters.balanceId || filters.directionId }
            : {}),
          ...(filters.status ? { status: filters.status } : {}),
        }),
      name: 'История списаний и начислений',
      alertText: 'Файл скачан',
    })
  }

  const {
    state,
    loading,
    getState: getData,
    setState,
    ...params
  } = usePagination({
    api: directionOrdersApi.getHistory,
    params: { ...getFilters() },
  })

  useEffect(() => {
    const handler = setTimeout(() => {
      getData()
      params.setCurrentPage(1)
    }, 250)

    return () => {
      clearTimeout(handler)
    }
  }, [sorting, userId, filters])

  const reportData = state as unknown as IFinancialTransactionsReportData
  const canConfirmManualPayments = MANUAL_PAYMENT_CONFIRM_ROLES.includes(
    profile?.role ?? '',
  )

  const handleRemove = (paymentRowId: number) => {
    Modal.confirm({
      title: 'Удалить строку?',
      content:
        'Строка будет удалена из отчёта. Если операция меняла баланс или срок доступа, изменение будет отменено.',
      okText: 'Удалить',
      cancelText: 'Отмена',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const response = await reportAPI.payments.paymentsRemove(paymentRowId)
          if (response?.data?.status !== 'success') throw new Error()
          addAlert({ title: 'Строка удалена' })
          await getData()
        } catch (error) {
          addAlert({ type: 'error', title: 'Не удалось удалить строку' })
        }
      },
    })
  }

  const handleConfirmManualPayment = (paymentId: number) => {
    Modal.confirm({
      title: 'Подтвердить ручную оплату?',
      content: 'Баланс курса будет обновлён согласно этой операции.',
      okText: 'Подтвердить',
      cancelText: 'Отмена',
      onOk: async () => {
        try {
          const response = await supportAPI.changeStatusManualPayment(paymentId, 1)
          if (response?.data?.status !== 'success') throw new Error()
          addAlert({ title: 'Ручная оплата подтверждена' })
          await getData()
        } catch (error) {
          addAlert({ type: 'error', title: 'Не удалось подтвердить ручную оплату' })
        }
      },
    })
  }

  return (
    <div className={cn.content}>
      <div className={cn.toolbar}>
        <FinancialTransactionsReportFilters {...{filters, setFilters, userId}} />
        {showDownload && (
          <Button
            color="gray"
            onClick={handleDownloadExcel}
            disabled={downloadLoading}
            isLoading={downloadLoading}
            leftContent={<IcoDownload style={{ transform: 'rotate(180deg)' }} />}
          >
            Скачать Excel
          </Button>
        )}
      </div>
      <ExerciseHomeworkReportTable
        loading={loading}
        data={reportData}
        sorting={sorting}
        onSortingClick={onSortingClick}
        canRemove={profile?.role === 'admin'}
        onRemove={handleRemove}
        canConfirmManualPayments={canConfirmManualPayments}
        onConfirmManualPayment={handleConfirmManualPayment}
      />
      <Pagination {...{ ...params }} />
    </div>
  )
}

export default FinancialTransactionsReport
